import { useEffect, useRef, useCallback } from 'react';
import { disconnectWallet } from '../web3';

const STORAGE_KEY = 'adminSessionTimeoutEnabled';
export const SESSION_TIMEOUT_MINUTES_KEY = 'adminSessionTimeoutMinutes';
const DEFAULT_MINUTES = 30;
const MIN_MINUTES = 1;
const MAX_MINUTES = 720; // 12 hours
const THROTTLE_MS = 1000; // Only reset timer at most once per second

/** Session timeout is only active when the user has turned it ON in Settings. */
export function isSessionTimeoutEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

/** Session timeout duration in minutes (default 30). Clamped between MIN_MINUTES and MAX_MINUTES. */
export function getSessionTimeoutMinutes(): number {
  if (typeof window === 'undefined') return DEFAULT_MINUTES;
  const stored = localStorage.getItem(SESSION_TIMEOUT_MINUTES_KEY);
  const n = parseInt(stored ?? '', 10);
  if (Number.isNaN(n)) return DEFAULT_MINUTES;
  return Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, n));
}

/**
 * When authenticated and session timeout is enabled, logs out after the configured minutes of inactivity.
 * Listens for mouse, keyboard, touch, and scroll to reset the timer.
 */
export function useInactivityLogout(isAuthenticated: boolean) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetRef = useRef<number>(0);

  const clearSession = useCallback(() => {
    sessionStorage.setItem('sessionExpired', '1');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    disconnectWallet();
    window.location.href = '/';
  }, []);

  const resetTimer = useCallback(() => {
    if (!isSessionTimeoutEnabled()) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const minutes = getSessionTimeoutMinutes();
    const inactivityMs = minutes * 60 * 1000;
    timeoutRef.current = setTimeout(clearSession, inactivityMs);
    lastResetRef.current = Date.now();
  }, [clearSession]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const startOrClear = () => {
      if (!isSessionTimeoutEnabled()) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return;
      }
      resetTimer();
    };

    startOrClear();

    const throttledReset = () => {
      if (!isSessionTimeoutEnabled()) return;
      if (Date.now() - lastResetRef.current < THROTTLE_MS) return;
      resetTimer();
    };

    const onSettingChange = () => startOrClear();
    window.addEventListener('adminSessionTimeoutChanged', onSettingChange);

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((ev) => window.addEventListener(ev, throttledReset));

    return () => {
      window.removeEventListener('adminSessionTimeoutChanged', onSettingChange);
      events.forEach((ev) => window.removeEventListener(ev, throttledReset));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, resetTimer]);
}
